import { useState, useEffect, useCallback } from 'react';
import { eventService, Event, EventFilters, PaginationOptions } from '../services/eventService';

interface UseEventsResult {
  events: Event[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  searchEvents: (searchTerm: string) => Promise<void>;
}

interface UseEventsOptions {
  filters?: EventFilters;
  initialLimit?: number;
  realTime?: boolean;
}

export function useEvents(options: UseEventsOptions = {}): UseEventsResult {
  const { filters, initialLimit = 10, realTime = false } = options;
  
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  const fetchEvents = useCallback(async (isLoadMore = false) => {
    try {
      if (!isLoadMore) {
        setLoading(true);
        setError(null);
      }

      const pagination: PaginationOptions = {
        limit: initialLimit,
        lastDoc: isLoadMore ? lastDoc : undefined
      };

      const result = await eventService.fetchEvents(filters, pagination);
      
      if (isLoadMore) {
        setEvents(prev => [...prev, ...result.events]);
      } else {
        setEvents(result.events);
      }
      
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
      if (!isLoadMore) {
        setEvents([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, initialLimit, lastDoc]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchEvents(true);
  }, [hasMore, loading, fetchEvents]);

  const refresh = useCallback(async () => {
    setLastDoc(null);
    setIsSearching(false);
    await fetchEvents(false);
  }, [fetchEvents]);

  const searchEvents = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      await refresh();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setIsSearching(true);
      
      const searchResults = await eventService.searchEvents(searchTerm, filters);
      setEvents(searchResults);
      setHasMore(false); // Search results don't support pagination
      setLastDoc(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filters, refresh]);

  // Initial fetch
  useEffect(() => {
    if (!isSearching) {
      fetchEvents(false);
    }
  }, [fetchEvents, isSearching]);

  // Real-time subscription
  useEffect(() => {
    if (!realTime || isSearching) return;

    const unsubscribe = eventService.subscribeToEvents(
      (newEvents) => {
        setEvents(newEvents);
        setLoading(false);
        setError(null);
      },
      filters,
      initialLimit
    );

    return unsubscribe;
  }, [realTime, filters, initialLimit, isSearching]);

  return {
    events,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    searchEvents
  };
}

interface UseEventResult {
  event: Event | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useEvent(eventId: string): UseEventResult {
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvent = useCallback(async () => {
    if (!eventId) {
      setEvent(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const fetchedEvent = await eventService.fetchEventById(eventId);
      setEvent(fetchedEvent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch event');
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const refresh = useCallback(async () => {
    await fetchEvent();
  }, [fetchEvent]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  return {
    event,
    loading,
    error,
    refresh
  };
}